import * as React from 'react';

import { translate } from '~l10n/client';

import { SmallButton } from '~shared/components/buttons';
import { Container } from '~shared/components/Container';
import { Spinner } from '~shared/components/Spinner';

interface Props {
  loading: boolean;
  data: number;
  isViewerRole: boolean;
  createFolder: () => void;
  openAddQuizDialog: () => void;
  workspaceId: string;
  searchQuery?: string; // Show a 'Nothing Found' state when searchQuery is present
}

export function WorkspaceQuizzesEmptyState({
  loading,
  data,
  isViewerRole,
  openAddQuizDialog,
  createFolder,
  workspaceId,
  searchQuery,
}: Props) {
  if (!(loading || data === 0)) {
    return null;
  }
  const hasSearch = !!searchQuery?.trim();
  // This is a workaround to ensure translation and UI is in sync for languages where subject is first (e.g. Korean)
  const translationTemplateString = translate('Nothing found matching “{0}”');
  // Pull out text before/after the {0}, stripping any surrounding quotes. Helps with languages that uses „…“ (U+201E / U+201C) quotes (e.g. German)
  const matchResult = translationTemplateString.match(/^(?<before>[\s\S]*?)\s*(?:["“”„«»‹›‚'’「『]?\s*)\{0\}(?:\s*["“”„«»‹›‚'’」』]?)\s*(?<after>[\s\S]*)$/u);
  const before = matchResult?.groups?.before ?? translationTemplateString;  // fallback: whole string if no match
  const after  = matchResult?.groups?.after  ?? '';

  return (
    <Container
      style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.1)' }}
      className={'w-full min-h-70 flex items-center justify-center p-5 min-w-0'}
    >
      <Container horizontalCenter verticalCenter className={'w-full'}>
        {loading ? (
          <>
            <Spinner size={'small'} />
          </>
        ) : hasSearch ? (
          <>
            <div className="p-10 flex flex-col justify-start min-w-0 text-center">
              {/* tooltip shows the full query if it is longer than 60 characters */}
              <h3 className="m-0 font-display text-blue-900 text-md font-400 leading-16 text-center">
                {before}
                <br />
                <q dir="auto">
                  <span
                    className="inline-block truncate align-bottom"
                    style={{ maxWidth: '60ch' }}
                    title={searchQuery}>
                    {searchQuery}
                  </span>
                </q>
                <br />
                {after}
              </h3>
              <p className="text-sm font-300 mt-2">
                {translate('Delete search terms to start over.')}
              </p>
            </div>
          </>
        ) : (
          <>
            <div
              className={
                'pb-6 flex justify-center content-center text-md font-display text-blue-900'
              }
            >
              {translate('This folder is empty')}
            </div>
            <div
              className={'text-sm w-full pb-8'}
              style={{ textAlign: 'center' }}
              dangerouslySetInnerHTML={{
                __html: translate(
                  'Add quizzes to get started! Visit the {0} page to invite others.'
                ).format(
                  `<a class="group relative no-underline hover:underline rounded-1 outline-none transition ease-in-out duration-150 font-500 text-cyan-700 hocus:text-cyan-600" href="#workspaces/${workspaceId}/members">${translate(
                    'Members'
                  )}</a>`
                ),
              }}
            />
            <Container row className={'pb-6'}>
              <SmallButton
                onClick={openAddQuizDialog}
                isDisabled={isViewerRole}
                className="mx-3"
              >
                {translate('Add Quiz')}
              </SmallButton>
              <SmallButton
                onClick={createFolder}
                isDisabled={isViewerRole}
                className="mx-3"
              >
                {translate('New Folder')}
              </SmallButton>
            </Container>
          </>
        )}
      </Container>
    </Container>
  );
}
